import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GoogleDriveSyncPanel = ({ 
  syncStatus, 
  lastSyncTime, 
  folderPath,
  onManualSync,
  onConfigureSync 
}) => {
  const formatSyncTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getSyncStatusConfig = () => {
    switch (syncStatus) {
      case 'syncing':
        return {
          icon: 'RefreshCw',
          color: 'text-accent',
          bgColor: 'bg-accent/10',
          label: 'Syncing...',
          animate: true
        };
      case 'synced':
        return {
          icon: 'CheckCircle2',
          color: 'text-success',
          bgColor: 'bg-success/10',
          label: 'Synced',
          animate: false
        };
      case 'error':
        return {
          icon: 'AlertCircle',
          color: 'text-destructive',
          bgColor: 'bg-destructive/10',
          label: 'Sync Failed',
          animate: false
        };
      default:
        return {
          icon: 'Cloud',
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          label: 'Not Synced',
          animate: false
        };
    }
  };

  const statusConfig = getSyncStatusConfig();

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-start justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg font-semibold mb-1">
            Google Drive Sync
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Automatic media synchronization
          </p>
        </div>
        
        <div className={`p-2 rounded-lg ${statusConfig?.bgColor}`}>
          <Icon 
            name={statusConfig?.icon} 
            size={20} 
            className={`${statusConfig?.color} ${statusConfig?.animate ? 'animate-spin' : ''}`}
          />
        </div>
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 md:p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Icon name="Folder" size={20} className="text-primary flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground mb-1">Synced Folder</p>
              <p className="text-sm font-medium truncate">{folderPath}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="Settings"
            onClick={onConfigureSync}
            className="flex-shrink-0 ml-2"
          >
            <span className="hidden md:inline">Configure</span>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div className="p-3 md:p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Last Sync</span>
            </div>
            <p className="text-sm font-medium">{formatSyncTime(lastSyncTime)}</p>
          </div>

          <div className="p-3 md:p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Activity" size={16} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Status</span>
            </div>
            <p className={`text-sm font-medium ${statusConfig?.color}`}>
              {statusConfig?.label}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-2">
          <Button
            variant="default"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={onManualSync}
            disabled={syncStatus === 'syncing'}
            loading={syncStatus === 'syncing'}
            className="flex-1"
          >
            Sync Now
          </Button>
          <Button
            variant="outline"
            iconName="FolderOpen"
            iconPosition="left"
            onClick={() => window.open('https://drive.google.com', '_blank')}
            className="flex-1"
          >
            Open Drive
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoogleDriveSyncPanel;